import BottomSheet, { BottomSheetFlatList } from '@gorhom/bottom-sheet';
import i18n from '@/utils/i18n';
import PhoneIcon from '@assets/icons/phone.svg';
import React, { useCallback, useMemo, useRef, useState } from 'react';
import {
  AffordanceHandleIndicator,
  BottomSheetBackdrop,
} from '@/components/bottom-sheet-backdrop';
import { AnalyticEvent } from '@/analytics/fennel/registry';
import { Avatar, createAvatarProps } from '@usepepper/ui/atoms/avatar';
import { createStyles } from '@/contexts/theme-context';
import { Dimensions, Modal, Platform, TouchableOpacity } from 'react-native';
import { fennelTrack } from '@/analytics/fennel/hook';
import { formatPhoneNumber, onCallMobilePhone } from '@/utils/phone-number';
import { GestureHandlerRootView } from 'react-native-gesture-handler';
import { ListItem } from '@usepepper/ui/molecules/list-item';
import { ListItemSeparator } from '@/components/list-item';
import { OperatorEmployeeContact } from '@/types';
import { useBottomSheetBackHandler } from '@/hooks/use-bottom-sheet-back-handler';
import { useSafeAreaInsets } from 'react-native-safe-area-context';

interface Props {
  contacts: OperatorEmployeeContact[];
  onDismiss: () => void;
}

// height of list item is 61 + 16 padding top and bottom
const ITEM_HEIGHT = 77;
const MAX_HEIGHT = Dimensions.get('window').height * 0.8;

export const PhonePickerBottomSheet: React.FC<Props> = ({
  contacts,
  onDismiss,
}) => {
  const [styles, theme] = useStyles();
  const { bottom } = useSafeAreaInsets();

  const bottomSheetRef = useRef<BottomSheet>(null);
  const { handleSheetPositionChange } =
    useBottomSheetBackHandler(bottomSheetRef);

  const [contentHeight, setContentHeight] = useState(ITEM_HEIGHT);

  const snapPoints = useMemo(() => {
    const height = Math.max(contentHeight, ITEM_HEIGHT);
    const minHeight = Math.min(height, MAX_HEIGHT);
    return [minHeight, MAX_HEIGHT];
  }, [contentHeight]);

  const isFixed = contentHeight < MAX_HEIGHT;
  // pick index: if content < max height, start at 0 (content height only), otherwise start at 1 (max height)
  const initialIndex = isFixed ? 0 : 1;

  const handleCall = useCallback(async (contact: OperatorEmployeeContact) => {
    await onCallMobilePhone(contact.phone_number);
    fennelTrack(AnalyticEvent.CURRENT_ORDERS_CALL, {
      phoneNumber: contact.phone_number,
      platform: Platform.OS,
      userUUID: contact.user_id,
    });
    bottomSheetRef.current?.close();
  }, []);

  return (
    <Modal
      accessibilityLabel={i18n.t('insights_hub.current_orders_section.sheet')}
      animationType="fade"
      onRequestClose={onDismiss}
      transparent
      visible
    >
      <GestureHandlerRootView style={styles.gestureHandler}>
        <BottomSheet
          backdropComponent={BottomSheetBackdrop}
          backgroundStyle={styles.background}
          enableContentPanningGesture={!isFixed}
          enablePanDownToClose
          handleComponent={AffordanceHandleIndicator}
          index={initialIndex}
          onChange={handleSheetPositionChange}
          onClose={onDismiss}
          ref={bottomSheetRef}
          snapPoints={snapPoints}
          style={styles.container}
        >
          <BottomSheetFlatList
            // to avoid touching the native home indicator and give some breathing room
            contentContainerStyle={{ paddingBottom: bottom }}
            data={contacts}
            ItemSeparatorComponent={() => (
              <ListItemSeparator style={styles.separator} />
            )}
            keyExtractor={(item) => item.user_id}
            onContentSizeChange={(_, height) => setContentHeight(height)}
            renderItem={({ item }) => (
              <ListItem
                leftElement={
                  <Avatar {...createAvatarProps(item.name, item.user_id, 2)} />
                }
                rightElement={
                  <TouchableOpacity
                    accessibilityLabel={i18n.t(
                      'insights_hub.current_orders_section.call',
                    )}
                    onPress={() => handleCall(item)}
                    style={styles.actionsElement}
                  >
                    <PhoneIcon
                      stroke={theme.sk.color.icon.primary}
                      width={40}
                    />
                  </TouchableOpacity>
                }
                style={styles.listItem}
                subtitle={formatPhoneNumber(item.phone_number)}
                title={item.name}
              />
            )}
            showsVerticalScrollIndicator={false}
          />
        </BottomSheet>
      </GestureHandlerRootView>
    </Modal>
  );
};

const useStyles = createStyles((theme) => ({
  actionsElement: {
    alignItems: 'center',
    backgroundColor: theme.sk.color.bg.tertiary.default,
    borderRadius: 20,
    height: 40,
    justifyContent: 'center',
    width: 40,
  },
  background: {
    backgroundColor: theme.sk.color.bg.primary.default,
  },
  container: {
    paddingHorizontal: 16,
  },
  gestureHandler: {
    flex: 1,
  },
  listItem: {
    paddingVertical: 16,
  },
  separator: {
    borderBottomColor: theme.sk.color.divider.default,
    borderBottomWidth: 1,
  },
}));
